import React from "react";
import { App as AntdApp, Button, Card, DatePicker, Form, Input, Modal, Select, Tag, Typography } from "antd";
import dayjs from "dayjs";

import { loadOrgTree } from "@modules/org/api/orgApi.js";
import { listPromos, createFlyerInPromo } from "@modules/promos/api/promoApi.js";

const { Title, Text } = Typography;

/**
 * Modal semplificato per creare un nuovo volantino dalla pagina /flyers.
 * In questo contesto non c'è una promo selezionata: la prima cosa da scegliere
 * è la promo di destinazione. Consorzio/Cooperativa sono ereditati dalla promo
 * (come in CreateFlyerInPromoModal); si seleziona solo il canale e le date.
 * L'elaborazione AI avviene tramite la pre-elaborazione del PDF.
 */
export default function CreateFlyerWizard({ open, onClose, onFlyerCreated }) {
	const { message } = AntdApp.useApp();
	const [form] = Form.useForm();
	const [promos, setPromos] = React.useState([]);
	const [selectedPromo, setSelectedPromo] = React.useState(null);
	const [canali, setCanali] = React.useState([]);
	const [flyerId, setFlyerId] = React.useState(null);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const hasBeenOpenRef = React.useRef(false);

	React.useEffect(() => {
		if (!open) return;
		(async () => {
			try {
				const items = await listPromos();
				// Escludo le promo legacy: non sono una destinazione valida per nuovi volantini.
				setPromos(items.filter(p => !p.isLegacy));
			}
			catch {
				setPromos([]);
			}
		})();
	}, [open]);

	React.useEffect(() => {
		if (open) {
			hasBeenOpenRef.current = true;
			return;
		}

		if (hasBeenOpenRef.current) {
			form.resetFields();
			setFlyerId(null);
			setSelectedPromo(null);
			setCanali([]);
			hasBeenOpenRef.current = false;
		}
	}, [open, form]);

	const onPromoChange = (promoId) => {
		form.setFieldsValue({ canaleId: null });
		const promo = promos.find(p => String(p._id || p.id) === String(promoId)) || null;
		setSelectedPromo(promo);

		// Prefill date dal Sell-Out della promo, se disponibili.
		if (promo?.sellOutFrom && promo?.sellOutTo) {
			form.setFieldsValue({ range: [dayjs(promo.sellOutFrom), dayjs(promo.sellOutTo)] });
		}

		if (!promo?.cooperativaId) {
			setCanali([]);
			return;
		}

		loadOrgTree().then(items => {
			const opts = items
				.filter(item => item.type === "CANALE" && String(item.parentId) === String(promo.cooperativaId))
				.map(item => ({ label: item.name, value: String(item._id) }));
			setCanali(opts);
		});
	};

	const onFilter = (input, option) => {
		return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
	};

	const onCreateFlyer = async () => {
		if (isSubmitting) return;
		setIsSubmitting(true);

		try {
			const v = await form.validateFields();
			const payload = {
				name: String(v.name || "").trim(),
				canaleId: v.canaleId,
				sellOutFrom: v.range?.[0] ? v.range[0].format("YYYY-MM-DD") : null,
				sellOutTo: v.range?.[1] ? v.range[1].format("YYYY-MM-DD") : null
			};

			const data = await createFlyerInPromo(v.promoId, payload);
			setFlyerId(data.id);
			message.success("Volantino creato! Apri la pre-elaborazione per tracciare le gabbie e avviare l'AI.");
			onFlyerCreated?.(data.id);
			onClose?.();
		}
		catch (error) {
			if (error?.errorFields) {
				// Errore di validazione form, non fare nulla (il form mostra già gli errori)
			}
			else {
				message.error(error?.response?.data?.error || "Errore durante la creazione del volantino");
			}
		}
		finally {
			setIsSubmitting(false);
		}
	};

	const promoOptions = promos.map(p => {
		const sub = [p.consorzioName, p.cooperativaName].filter(Boolean).join(" / ");
		return {
			label: sub ? `${p.name} — ${sub}` : p.name,
			value: String(p._id || p.id)
		};
	});

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width={500}
			maskClosable={false}
			className="sis-modal"
			title="Nuovo Volantino"
			destroyOnHidden
		>
			<Card className="card-elev">
				<Title level={5} style={{ marginBottom: 16 }}>Metadati Volantino</Title>
				<Form form={form} layout="vertical" onFinish={onCreateFlyer}>
					<Form.Item label="Promo" name="promoId" rules={[{ required: true, message: "Seleziona la promo di destinazione" }]}>
						<Select
							placeholder="Seleziona la promo"
							showSearch
							options={promoOptions}
							onChange={onPromoChange}
							allowClear
							filterOption={onFilter}
						/>
					</Form.Item>
					{selectedPromo && (
						<Text type="secondary" style={{ display: "block", marginTop: -8, marginBottom: 16 }}>
							Consorzio/Cooperativa ereditati dalla promo
							{[selectedPromo.consorzioName, selectedPromo.cooperativaName].filter(Boolean).length
								? `: ${[selectedPromo.consorzioName, selectedPromo.cooperativaName].filter(Boolean).join(" / ")}`
								: ""}
						</Text>
					)}
					<Form.Item
						label="Nome Volantino"
						name="name"
						rules={[{ required: true, whitespace: true, message: "Inserisci il nome del volantino" }]}
					>
						<Input placeholder="Es. Volantino Primavera 2025" />
					</Form.Item>
					<Form.Item label="Canale" name="canaleId" rules={[{ required: true, message: "Seleziona un canale" }]}>
						<Select disabled={!selectedPromo || !canali.length} placeholder="Seleziona canale" options={canali} showSearch allowClear filterOption={onFilter} />
					</Form.Item>
					<Form.Item label="Sell-Out" name="range" rules={[{ required: true, message: "Seleziona l'intervallo Sell-Out" }]}>
						<DatePicker.RangePicker format="DD/MM/YYYY" placeholder={["Dal", "Al"]} style={{ width: "100%" }} />
					</Form.Item>
					<Button
						type="primary"
						htmlType="submit"
						size="large"
						block
						loading={isSubmitting}
						disabled={isSubmitting}
					>
						Crea Volantino
					</Button>
					{flyerId && <Tag color="blue" style={{ marginTop: 8 }}>ID: {flyerId}</Tag>}
				</Form>
			</Card>
		</Modal>
	);
}
